import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import FunnelLayout from "./FunnelLayout";
import "./CommonNewFunnel.css";

const STEP1_KEY = "glp1_intake_step1";
const STEP3_KEY = "glp1_intake_step3";

type Step1Saved = {
  weight?: string;
  goalWeight?: string;
};

type PaceChoice = "" | "works" | "faster" | "too_fast";

// ✅ Step 3 now saves FULL ANSWER string (not pace code)
type Step3Saved = {
  Q4_How_is_that_pace_for_you?: string; // "That works for me" | "I want it faster" | "That’s too fast"
  weeksWorks?: number;
  weeksFaster?: number;
};

const QUESTION_KEY = "Q4_How_is_that_pace_for_you";

const PACE_LABEL = {
  works: "That works for me",
  faster: "I want it faster",
  too_fast: "That’s too fast",
} as const;

const fullAnswerToPace = (answer: string): PaceChoice => {
  if (answer === PACE_LABEL.works) return "works";
  if (answer === PACE_LABEL.faster) return "faster";
  if (answer === PACE_LABEL.too_fast) return "too_fast";
  return "";
};

const NewFunnelStep4: React.FC = () => {
  const navigate = useNavigate();

  const { lbsToLose, weeks, pace } = useMemo(() => {
    let weight = 0;
    let goalWeight = 0;

    let paceVal: PaceChoice = "";
    let weeksVal = 0;

    // ✅ Step 1
    const s1 = localStorage.getItem(STEP1_KEY);
    if (s1) {
      try {
        const p = JSON.parse(s1) as Step1Saved;
        weight = Number(p.weight || 0);
        goalWeight = Number(p.goalWeight || 0);
      } catch {}
    }

    // ✅ Step 3 (NEW SHAPE)
    const s3 = localStorage.getItem(STEP3_KEY);
    if (s3) {
      try {
        const p = JSON.parse(s3) as Step3Saved;

        const fullAnswer = String(p?.[QUESTION_KEY] || "");
        paceVal = fullAnswerToPace(fullAnswer);

        if (paceVal === "faster") weeksVal = Number(p.weeksFaster || 0);
        else if (paceVal === "works") weeksVal = Number(p.weeksWorks || 0);
        else weeksVal = 0; // too_fast doesn't show weeks
      } catch {}
    }

    return {
      lbsToLose: Math.max(0, weight - goalWeight),
      weeks: weeksVal,
      pace: paceVal,
    };
  }, []);

  return (
    <FunnelLayout>
      <div className="perfect-wrap">
        {/* ✅ WORKS FOR ME */}
        {pace === "works" && (
          <>
            <h1 className="perfect-title">Perfect!</h1>
            <div className="perfect-text">
              Losing {lbsToLose} lbs is easier than
              <br />
              you think – and it
              <br />
              <span className="perfect-text__blue">
                doesn’t involve restrictive diets.
              </span>
            </div>
          </>
        )}

        {/* ✅ FASTER */}
        {pace === "faster" && (
          <>
            <h1 className="perfect-title">
              Not a problem,
              <br />
              <p style={{ marginBottom: "40px" }} className="perfect-text__blue">
                we can move faster.
              </p>
            </h1>

            <div className="perfect-text">
              It will take some work, but with GLP-1 medication,
              <br />
              your goal to lose {lbsToLose} lbs can be achieved
              <br />
              in about {weeks || "--"} weeks – and it
              <br />
              <span className="perfect-text__blue">
                doesn’t involve restrictive diets.
              </span>
            </div>
          </>
        )}

        {/* ✅ TOO FAST */}
        {pace === "too_fast" && (
          <>
            <h1 className="perfect-title">
              We&apos;ll move at <br />
              <p style={{ marginBottom: "40px" }} className="perfect-text__blue">
                your pace.
              </p>
            </h1>

            <div className="perfect-text">
              With GLP-1 medication, your goal to lose {lbsToLose} lbs
              <br />
              is easier than you think – and it
              <br />
              <span className="perfect-text__blue">
                doesn’t involve restrictive diets.
              </span>
            </div>
          </>
        )}

        {/* ✅ Safety fallback (if step3 missing) */}
        {!pace && (
          <>
            <h1 className="perfect-title">Almost there!</h1>
            <div className="perfect-text">
              Please go back and select your pace.
            </div>
          </>
        )}

        <div className="perfect-divider" />

        <button
          className="next-btn"
          onClick={() =>
            pace
              ? navigate("/glp1/intake-form/step-4")
              : navigate("/glp1/intake-form/step-3")
          }
        >
          Next →
        </button>
      </div>
    </FunnelLayout>
  );
};

export default NewFunnelStep4;